import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig } from 'axios';
import { setHeaders } from '../services/apiService';
import { getAuthToken } from '../services/authService';
import { handleHTTPError } from '../pages/errorHandler/errorHandlerHelper';

type ApiThunkArgs = {
  url: string;
  query?: string;
  method?: string;
  data?: any;
  params?: Record<string, any>;
  variables?: Record<string, any>;
  headers?: Record<string, string>;
};

export type ApiThunkRejectPayload = {
  message: string;
  errorCode?: string | number;
};

const getApiErrorDetails = (err: any): ApiThunkRejectPayload => {
  const data = err?.response?.data ?? {};
  const graphQLError = Array.isArray(data?.errors) && data.errors.length > 0 ? data.errors[0] : {};
  const graphQLErrorExtensions = graphQLError?.extensions ?? {};
  const message =
    data?.message ||
    graphQLErrorExtensions?.errorMessage ||
    graphQLError?.message ||
    err?.message ||
    'API Error';
  const errorCode =
    data?.code || graphQLErrorExtensions?.code || graphQLErrorExtensions?.classification;

  return { message, errorCode };
};

export function createApiThunk<T = any>(
  typePrefix: string,
  apiFn: (args: ApiThunkArgs) => Promise<T>,
) {
  return createAsyncThunk<T, ApiThunkArgs>(typePrefix, async (args, thunkAPI) => {
    try {
      return await apiFn(args);
    } catch (err: any) {
      handleHTTPError(err, thunkAPI.dispatch as AppDispatch, args?.params?.errorMessage);
      return thunkAPI.rejectWithValue(getApiErrorDetails(err));
    }
  });
}

export async function apiCall({
  url,
  method = 'GET',
  data,
  params,
  headers = {},
}: {
  url: string;
  method?: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
}) {
  let config: AxiosRequestConfig = {
    url,
    method,
    headers: {
      ...headers,
    },
    data,
    params,
  };
  config = setHeaders(config);

  const response = await axios(config);
  return response.data;
}

export async function graphqlCall({
  url,
  query,
  variables = {},
  headers = {},
}: {
  url: string;
  query: string;
  variables?: Record<string, any>;
  headers?: Record<string, string>;
}) {
  let config: AxiosRequestConfig = {
    url,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getAuthToken()}`,
      ...headers,
    },
    data: JSON.stringify({ query, variables }),
  };
  config = setHeaders(config);

  const response = await axios(config);
  const graphQLErrors = response?.data?.errors;
  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    const firstError = graphQLErrors[0] ?? {};
    const errorExtensions = firstError?.extensions ?? {};
    const message =
      errorExtensions?.errorMessage ||
      firstError?.message ||
      response?.data?.message ||
      'GraphQL Error';
    const code = errorExtensions?.code || errorExtensions?.classification || response?.data?.code;
    throw {
      message,
      response: {
        data: {
          ...response?.data,
          message,
          code,
        },
      },
    };
  }
  return response.data;
}
